import React from 'react';
import styles from './HistoryStylePane.styles';
import { DispatchProps, StateProps } from './HistoryStylePane.container';
import { TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { StylePane } from 'configuration-schemas/viewdefns/viewdefn';
import Renderer, { decorateTextByClasses } from 'src/utils/Domain/Renderer';
import { ColorSwatchItemProps } from 'src/common-ui/components/ColorSwatch/ColorSwatchItem';
import { STYLE_ID, CC_COLOR, STYLE_DESCRIPTION, STYLE_NAME, STYLE_COLOR_NAME } from 'src/utils/Domain/Constants';
import { classes } from 'typestyle';
import HistoryStylePaneCollapsableGroup from 'src/components/HistoryStylePane/HistoryStylePaneCollapsableGroup';
import * as globalMath from 'mathjs';

import { resolvePath } from 'src/cdn';
import ValidOptions from 'src/components/ValidOption/ValidOptions';
import { STYLE_COLOR_ID } from '../../utils/Domain/Constants';
import { size, isString, isEmpty, isNil } from 'lodash';
import { arrayStringToArray } from 'src/utils/Primitive/String';
import { getSwatchUrl } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import ServiceContainer from 'src/ServiceContainer';

import noImagePath from 'src/common-ui/images/noimage.jpg';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import CenteredImage from '../CenteredImage/CenteredImage';
import {
  checkKeyAsyncState,
  columnConfigToEditableField,
  handleDividedColumnDetailsDropdownChange,
} from 'src/pages/AssortmentBuild/StyleEdit/DividedColumnDetailsSection/DividedColumnDetails.utils';
import {
  DividedColumnDetailsSectionColumn,
  ValueSelection,
} from 'src/pages/AssortmentBuild/StyleEdit/DividedColumnDetailsSection/DividedColumnDetails.types';
import { AsyncCellState } from 'src/components/ConfigurableGrid/ConfigurableGrid.types';
import { resolveStylePaneConfig } from './HistoryStylePane.types';
import { NumberedWeek } from 'src/common-ui/components/WeekRange/WeekRangePicker.interface';
import Skeleton from '@material-ui/lab/Skeleton';

const noImage = resolvePath(noImagePath);

// A single entry in a StylePane's `view`. The looseObject passthrough on zStylePane
// carries the rendering fields (xtype, classes, editable, columns) this component relies on.
type StylePaneViewItem = StylePane['view'][number];

// Most tenant style panes are four groups of similarly-sized attribute rows. This
// gives the pane useful height on its very first render, before its tenant config
// is available. As soon as that config arrives, it replaces this shape.
const DEFAULT_SKELETON_GROUP_ROW_COUNTS = [5, 5, 4, 4];

const defaultSkeletonViewDefn: StylePane = {
  segment: '',
  model: '',
  view: DEFAULT_SKELETON_GROUP_ROW_COUNTS.map((rowCount, groupIndex) => ({
    text: `skeleton-group-${groupIndex}`,
    editable: false,
    columns: Array.from({ length: rowCount }, (_, rowIndex) => ({
      text: `skeleton-row-${groupIndex}-${rowIndex}`,
      dataIndex: `skeleton-row-${groupIndex}-${rowIndex}`,
      editable: false,
    })),
  })),
};

type PropValue = any;

export interface RequiredData {
  [STYLE_ID]: string;
  id: string;
}
interface Props extends StateProps, DispatchProps {
  dataId: string;
  styleColorId: string;
  styleColorName?: string;
  isAssortmentBuild: boolean;
  onDescriptionReady: (description: string) => void;
  onTitleReady: (title: string) => void;
  onDataReady: (data: BasicPivotItem[]) => void;
  onPinnedReady: (pinnedContent: JSX.Element | null) => void;
}
interface State {
  selectedPosition: number;
  defaultPosition: number;
  openDropdownKey: string | null;
}

const isMissingColorId = (swatch: ColorSwatchItemProps) => isNil(swatch.colorId) || isEmpty(swatch.colorId);

export default class HistoryStylePane extends React.Component<Props, State> {
  math = globalMath.create(globalMath.all) as globalMath.MathJsStatic;
  static getDerivedStateFromProps(props: Props) {
    const { currentItem, styleColorId } = props;
    let defaultPos = 0;
    if (currentItem !== null) {
      const position = currentItem.findIndex((item) => item.id === styleColorId);
      if (position > -1) {
        defaultPos = position;
      } else if (!isEmpty(props.styleColorName)) {
        const idx = currentItem.findIndex((x) => x[STYLE_COLOR_NAME] === props.styleColorName);
        defaultPos = idx;
      }
    }
    return {
      defaultPosition: defaultPos,
    };
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      selectedPosition: -1,
      defaultPosition: 0,
      openDropdownKey: null,
    };
  }

  componentDidMount() {
    const { dataId, onShowHistoryStylePane, isAssortmentBuild, stylePaneConfig } = this.props;
    onShowHistoryStylePane(dataId, isAssortmentBuild, stylePaneConfig);
  }

  componentDidUpdate(prevProps: Props) {
    const {
      dataId,
      currentItem,
      onShowHistoryStylePane,
      moveItemToHistory,
      isAssortmentBuild,
      stylePaneConfig,
      loading,
      viewDefn,
    } = this.props;
    if (prevProps.dataId !== dataId || prevProps.stylePaneConfig !== stylePaneConfig) {
      if (currentItem !== null && currentItem.length !== 0) {
        moveItemToHistory(dataId, currentItem);
      }
      onShowHistoryStylePane(dataId, isAssortmentBuild, stylePaneConfig);
    }

    if (loading && (!prevProps.loading || prevProps.viewDefn !== viewDefn)) {
      this.props.onPinnedReady(this.buildPinnedSkeleton(viewDefn ?? defaultSkeletonViewDefn));
    }
  }

  showEditor = (rendererKey: string) => this.setState({ openDropdownKey: rendererKey });
  showRenderer = () => this.setState({ openDropdownKey: null });

  // read the async (save/redecorate) state for a field on the currently displayed row
  getKeyAsyncState = (dataIndex: string | undefined) => {
    if (isNil(dataIndex)) {
      return AsyncCellState.Idle;
    }
    const { currentItem } = this.props;
    const row = currentItem ? currentItem[this.getCurrentPosition()] : undefined;
    return checkKeyAsyncState(dataIndex, row ?? undefined);
  };

  // map an async cell state to the global spinner class used by the editable cells
  getAsyncLoadingClass = (dataIndex: string | undefined): string | undefined => {
    switch (this.getKeyAsyncState(dataIndex)) {
      case AsyncCellState.Processing:
        return 'loading';
      case AsyncCellState.Redecorating:
        return 'redecorating';
      default:
        return undefined;
    }
  };

  getCurrentPosition = () => {
    const { selectedPosition, defaultPosition } = this.state;
    const { currentItem } = this.props;
    let position = defaultPosition;
    if (selectedPosition >= 0) {
      position = selectedPosition;
    }

    if (currentItem && position > currentItem.length - 1) {
      // state can be left over from the last style loaded
      // fallback here if position(is the index of array) is high than current length - 1
      position = defaultPosition;
    }
    return position;
  };

  buildSwatches = (items: BasicPivotItem[] | null) => {
    let swatches: ColorSwatchItemProps[] = [];
    if (items === null) {
      return swatches;
    }
    swatches = items.map((item, index) => {
      return {
        id: item[STYLE_COLOR_ID],
        colorId: item[CC_COLOR],
        noImageUrl: noImage,
        swatchPath: `${getSwatchUrl(item[CC_COLOR])}`,
        selected:
          this.state.selectedPosition > -1
            ? index === this.state.selectedPosition
            : index === this.state.defaultPosition,
      };
    });

    return swatches;
  };

  handleClickOnSwatch = (id: string, position: number) => {
    this.setState({
      selectedPosition: position,
    });
  };

  handleDropdownChange = (selection: ValueSelection, item: DividedColumnDetailsSectionColumn) => {
    const { dataId: styleId, currentItem: data, isAssortmentBuild } = this.props;
    const { dataIndex, inputType } = item;
    // if the selection is undefined/null, don't go through this (dependent dropdowns without options or clearing)
    if (isNil(selection) || isNil(data)) return;
    // need to send stylecolor for image edits
    const updateCoordinateMap =
      inputType === 'imageCropper' ? { product: 'stylecolor' } : this.props.viewDefn?.updateCoordinateMap;
    // edit the currently displayed style-color row, not blindly the first one
    const position = this.getCurrentPosition();
    const row = data ? data[position] : undefined;

    const maybeUpdatedData = handleDividedColumnDetailsDropdownChange({
      selection,
      item,
      updateCoordinateMap,
      styleId,
      data: row,
      dependentCalcs: {}, // FIXME
      mathInstance: this.math,
    });

    if (!maybeUpdatedData) return;

    const { updatedData, updatedValue, payloads } = maybeUpdatedData;

    if (isNil(updatedData) || isNil(updatedData.id)) return;

    // optimistically reflect the change in redux, then persist + redecorate
    this.props.localUpdateItem(updatedData, position);

    // redecorate uses the same data defnId the container fetched with
    const { dataDefnId: defnId, aggBy } = resolveStylePaneConfig(this.props.stylePaneConfig, isAssortmentBuild);

    this.props.saveDataFromPayloads(
      {
        payloads,
        styleId,
        dataIndex,
        updatedValue,
        updateCoordinateMap,
        row: updatedData,
        productId: updatedData.id,
        defnId,
        aggBy,
        position,
      },
      data,
    );
  };

  handleRangePickerChange = (range: NumberedWeek[], item: DividedColumnDetailsSectionColumn) => {
    const { currentItem: data, isAssortmentBuild } = this.props;
    // WeekRangePicker fires this twice on submit; the second call carries raw dates
    // without weekNos, so drop anything without a week selection (see RangePickerEditor.container)
    if (isNil(data) || isNil(range) || isEmpty(range) || isNil(range[0]) || isNil(range[0].weekNo)) return;

    // edit the currently displayed style-color row, not blindly the first one
    const position = this.getCurrentPosition();
    const row = data[position];
    if (isNil(row) || isNil(row.id)) return;

    // lifecycleParams expects the bare attribute name (eg: attribute:<x>:id => <x>)
    const formattedDataIndex = item.dataIndex.replace(/(member|attribute):/, '').replace(/:(id|name|description)/, '');
    const { dataDefnId, aggBy } = resolveStylePaneConfig(this.props.stylePaneConfig, isAssortmentBuild);

    this.props.updateLifecycleData(
      {
        styleColorId: row.id,
        newValue: { [formattedDataIndex]: range[0].weekNo },
        defnId: dataDefnId,
        aggBy,
        position,
      },
      data,
    );
  };

  xtypeRenderer(data: BasicPivotItem, viewDefnSlice: StylePaneViewItem) {
    const { classes } = viewDefnSlice;
    let renderedJsx;

    if (!viewDefnSlice.editable) {
      switch (viewDefnSlice.xtype) {
        case 'swatches':
          const swatches = this.buildSwatches(this.props.currentItem);
          const isMissingColorIds = swatches.some(isMissingColorId);
          if (isMissingColorIds) {
            const colorlessIds = swatches
              .filter(isMissingColorId)
              .map((swatch) => swatch.id)
              .join(',');
            ServiceContainer.loggingService.info(
              `No '${CC_COLOR}' value for following style color item(s): ${colorlessIds}`,
            );
          }

          renderedJsx = (
            <div>
              {Renderer.colorSwatch(
                swatches,
                this.handleClickOnSwatch,
                this.getCurrentPosition(),
                viewDefnSlice.pinned === 'top'
                  ? `${styles.historyStylePaneSwatches} ${styles.historyStylePanePinnedSwatchesMargin}`
                  : styles.historyStylePaneSwatches,
              )}
            </div>
          );
          break;
        case 'validoptions':
          const selection = data[viewDefnSlice.dataIndex ?? ''];
          const convertedSelection: string[] = isString(selection) ? arrayStringToArray(selection, false) : selection;
          const formattedSelection = !isEmpty(convertedSelection)
            ? convertedSelection.map((item) => item.replace(/"/g, ''))
            : [];
          renderedJsx = <ValidOptions editable={false} selected={formattedSelection} />;
          break;
        case 'image':
          const imgSrc: string = data[viewDefnSlice.dataIndex ?? ''] || noImage;
          renderedJsx = (
            <div data-qa={`${viewDefnSlice.dataIndex}-value`}>
              <CenteredImage src={imgSrc} width={styles.STYLE_PANE_IMG_WIDTH} height={styles.STYLE_PANE_IMG_HEIGHT} />
            </div>
          );
          break;
        case 'array':
          const propValue = data[viewDefnSlice.dataIndex ?? ''];
          let renderedValues: any[] = [];
          if (Array.isArray(propValue)) {
            renderedValues = propValue.map((value: PropValue) =>
              decorateTextByClasses(
                Renderer.renderJustValue(value, viewDefnSlice as TenantConfigViewItem),
                classes ?? [],
              ),
            );
          }
          renderedJsx = <div data-qa={`${viewDefnSlice.dataIndex}-value`}>{renderedValues.join(', ')}</div>;
          break;
        case 'string':
        default:
          const renderedValue = Renderer.renderValue(data, viewDefnSlice as TenantConfigViewItem);
          const decoratedValue = decorateTextByClasses(renderedValue, classes ?? []);
          if (!renderedValue) {
            renderedJsx = (
              <div data-qa={`${viewDefnSlice.dataIndex}-value`} dangerouslySetInnerHTML={{ __html: '&nbsp;' }} />
            );
          } else {
            renderedJsx = <div data-qa={`${viewDefnSlice.dataIndex}-value`}>{decoratedValue}</div>;
          }
      }
      return renderedJsx;
    }

    // @ts-ignore yeah I know the type isn't there yet
    // default override so we don't have to put this in config everywhere
    if (isEmpty(viewDefnSlice.inputParams)) {
      viewDefnSlice.inputParams = { height: 32 }; // 32 matches the height requested for headitors
    }

    return columnConfigToEditableField({
      // Style Pane allows undefined `text` fields (mainly for swatches which just show the boxes),
      // but DividedColumnDetails doesn't.  We only render editing fields here so it doesn't really matter
      // fix this later when DividedColumnDetails gets zod types
      // @ts-ignore
      item: viewDefnSlice,
      componentIndex: 99, // used by style edit for apply `key`s to elements, set to 99 here as it doesn't really matter in stylepane
      styleId: this.props.dataId, // this should be the `styleId`
      data: data,
      dropdownOptions: {}, // FIXME
      dependentCalcs: {}, // Only used by legacy style edit components
      mathInstance: this.math,
      updateCoordinateMap: this.props.viewDefn?.updateCoordinateMap,
      onCloseCallback: this.showRenderer,
      onClickCallback: this.showEditor,
      openDropdownKey: this.state.openDropdownKey,
      handleDropdownChange: this.handleDropdownChange,
      rangePickerData: {
        mergedRangeList: this.props.mergedRangeList,
        allowFrom: this.props.allowFrom,
        allowTo: this.props.allowTo,
      },
      handleRangePickerChange: this.handleRangePickerChange,
    });
  }

  generateSection = (data: BasicPivotItem, viewDefnSlice: StylePaneViewItem, indexValue: number) => {
    if (viewDefnSlice.columns) {
      const subcontent: JSX.Element[] = viewDefnSlice.columns.map((slice, index) => {
        return this.generateSection(data, slice as StylePaneViewItem, index);
      });
      return (
        <HistoryStylePaneCollapsableGroup
          key={indexValue}
          indexValue={indexValue}
          viewDefnSlice={viewDefnSlice as TenantConfigViewItem}
        >
          {subcontent}
        </HistoryStylePaneCollapsableGroup>
      );
    }
    let containerChildren;
    let specialClassName;
    const jsx = this.xtypeRenderer(data, viewDefnSlice);
    // spinner class while this field is being saved / redecorated
    const loadingClass = this.getAsyncLoadingClass(viewDefnSlice.dataIndex);

    if (viewDefnSlice.style === 'title') {
      specialClassName = styles.historyStylePaneTitle;
    } else if (viewDefnSlice.style === 'description') {
      specialClassName = styles.historyStylePaneDescription;
    } else if (viewDefnSlice.xtype === 'swatches') {
      specialClassName = styles.historyStylePaneSwatchContainer;
    }

    if (viewDefnSlice.text) {
      containerChildren = (
        <React.Fragment>
          <div className={styles.historyStylePaneLabel}>
            <div className={styles.historyStylePaneLabelText} data-qa={`${viewDefnSlice.dataIndex}-text`}>
              {viewDefnSlice.text || ''}
            </div>
          </div>
          <div className={classes(styles.historyStylePaneValue, loadingClass)}>{jsx}</div>
        </React.Fragment>
      );
    } else if (
      viewDefnSlice.dataIndex !== STYLE_ID &&
      viewDefnSlice.dataIndex !== STYLE_NAME &&
      viewDefnSlice.dataIndex !== STYLE_DESCRIPTION
    ) {
      containerChildren = loadingClass ? <div className={loadingClass}>{jsx}</div> : jsx;
    } else {
      // no longer showing title or desc
      containerChildren = null;
    }

    return (
      <div
        key={indexValue}
        className={classes(styles.historyStylePaneTuple, specialClassName)}
        data-qa={viewDefnSlice.text}
      >
        {containerChildren}
      </div>
    );
  };

  generateStructure = (data: BasicPivotItem, viewDefn: StylePane) => {
    return viewDefn.view.map((section, index) => {
      return this.generateSection(data, section, index);
    });
  };

  generateSkeletonSection = (viewDefn: StylePaneViewItem, indexValue: number): JSX.Element | null => {
    if (viewDefn.columns) {
      return (
        <div key={indexValue} className={styles.historyStylePaneGroup} data-qa="StylePaneSkeletonGroup">
          <div className={styles.historyStylePaneGroupTitleContainer}>
            <Skeleton animation="wave" variant="text" width="45%" height={24} />
          </div>
          <div className={styles.historyStylePaneGroupContent}>
            {viewDefn.columns.map((column, index) => this.generateSkeletonSection(column as StylePaneViewItem, index))}
          </div>
        </div>
      );
    }

    // These values are displayed in the fixed pane header, not in the body.
    if (
      viewDefn.dataIndex === STYLE_ID ||
      viewDefn.dataIndex === STYLE_NAME ||
      viewDefn.dataIndex === STYLE_DESCRIPTION
    ) {
      return null;
    }

    if (viewDefn.xtype === 'image') {
      return (
        <div key={indexValue} className={styles.historyStylePaneSkeletonImage} data-qa="StylePaneSkeletonItem">
          <Skeleton
            animation="wave"
            variant="rect"
            width={styles.STYLE_PANE_IMG_WIDTH}
            height={styles.STYLE_PANE_IMG_HEIGHT}
          />
        </div>
      );
    }

    if (viewDefn.xtype === 'swatches') {
      return (
        <div key={indexValue} className={styles.historyStylePaneSkeletonSwatches} data-qa="StylePaneSkeletonItem">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} animation="wave" variant="rect" width={20} height={20} />
          ))}
        </div>
      );
    }

    return (
      <div key={indexValue} className={styles.historyStylePaneTuple} data-qa="StylePaneSkeletonItem">
        <div className={styles.historyStylePaneLabel}>
          <Skeleton animation="wave" variant="text" width="75%" />
        </div>
        <div className={styles.historyStylePaneValue}>
          <Skeleton animation="wave" variant="text" width="65%" />
        </div>
      </div>
    );
  };

  buildSkeleton = (viewDefn: StylePane) =>
    viewDefn.view
      .filter((section) => section.pinned !== 'top')
      .map((section, index) => this.generateSkeletonSection(section, index));

  buildPinnedSkeleton = (viewDefn: StylePane): JSX.Element | null => {
    const pinnedSections = viewDefn.view.filter((section) => section.pinned === 'top');
    return isEmpty(pinnedSections) ? null : (
      <React.Fragment>
        {pinnedSections.map((section, index) => this.generateSkeletonSection(section, index))}
      </React.Fragment>
    );
  };

  buildNoData = () => (
    <div className={styles.historyStylePaneNoData} data-qa="StylePaneNoData" role="status">
      <div className={styles.historyStylePaneNoDataTitle}>No data available</div>
      <div>We couldn’t find any details for this style.</div>
    </div>
  );

  buildJsx = (data: BasicPivotItem[] | null, viewDefn: StylePane) => {
    const { onDescriptionReady, onTitleReady, onDataReady, onPinnedReady } = this.props;
    if (data === null || size(data) < 1) {
      // clear prior selection
      onDescriptionReady('');
      onTitleReady('');
      onDataReady([]);
      onPinnedReady(null);
      return this.buildNoData();
    }

    onDescriptionReady(data[0][STYLE_DESCRIPTION]);
    onTitleReady(data[0][STYLE_NAME]);
    onDataReady(data);

    // take items marked `pinned: 'top'` out of the scrollable body and move up to
    // the RightContainer to render inside its fixed header, alongside the title/description
    const pinnedView = viewDefn.view.filter((section) => section.pinned === 'top');
    const bodyViewDefn: StylePane = isEmpty(pinnedView)
      ? viewDefn
      : { ...viewDefn, view: viewDefn.view.filter((section) => section.pinned !== 'top') };

    const sections = data.map((item) => this.generateStructure(item, bodyViewDefn));
    const position = this.getCurrentPosition();

    onPinnedReady(
      isEmpty(pinnedView) ? null : (
        <React.Fragment>{this.generateStructure(data[position], { ...viewDefn, view: pinnedView })}</React.Fragment>
      ),
    );

    return sections[position];
  };

  render() {
    const { loading, viewDefn, currentItem } = this.props;
    const content =
      loading || !viewDefn
        ? this.buildSkeleton(viewDefn ?? defaultSkeletonViewDefn)
        : this.buildJsx(currentItem, viewDefn);

    return (
      <div className={styles.historyStylePane} data-qa="StylePaneBodyContainer">
        {content}
      </div>
    );
  }
}
